import classNames, { Argument } from "classnames/dedupe";
import React, { FC, useCallback, useRef } from "react";
import { CSSTransition } from "react-transition-group";

import {
  IconLink,
  IIconLinkProps,
  ILink,
  Link,
} from "../../ActionsSelectors/Link";
import { ILinkProps } from "../../ActionsSelectors/Link/Link";
import { IMediaBuilderProps, MediaBuilder } from "../../Utility/MediaBuilder";
import {
  DropdownAlignment,
  DropdownContext,
  DropdownDrawingStrategy,
  DropdownToggleStrategy,
  IDropdownProps,
  RectPos,
  IDropdownContext,
} from "../../Utility";
import DrawerMask from "../../Utility/DrawerMask/DrawerMask";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import DropdownList from "../../Utility/Dropdown/DropdownList";
import { IDropdownListProps } from "../../Utility/Dropdown/DropdownList.interface";

export type TLinkStyle = "default" | "light" | "icon";

export interface INavItemProps<LinkType> extends ILink<LinkType> {
  /**
   * The visual preset style of the NavItem component
   *
   * @type {('default'|'light'|'icon'')}
   *
   * @default 'default'
   */
  type?: TLinkStyle;

  /**
   * Existing SVG Icon component
   *
   *@type {ReactElement}
   */
  iconData?: IMediaBuilderProps;

  /** The content of the Link component.  */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  linkChildren?: any;

  /**
   * Set if item is currently selected, applies css class 'active'
   */
  active?: boolean;

  /**
   * Flag to generate nav item without dropdown wrapper
   */

  isSimpleNavItem?: boolean;

  /**
   * The custom set of css classes used to style component
   *
   */
  classes?: {
    /** Styles applied to outer 'li' element. */
    root?: Argument;
    link?: Argument;
    wrapper?: Argument;
    menu?: Argument;
    dropdownList?: Argument;
  };
  /** Override properties of `Dropdown` component */
  dropdownProps?: Partial<IDropdownProps>;
  /** Override properties of `DropdownList` component */
  dropdownListProps?: Partial<IDropdownListProps>;
  /** CSS OR JS render type */
  drawingStrategy?: IDropdownProps["drawingStrategy"];
  /** Additional mask offsets */
  maskOffset?: RectPos;
  children?: React.ReactNode;
}

function NavItem<LinkType>({
  classes,
  type = "default",
  active,
  linkChildren,
  children,
  iconData,
  linkParams,
  drawingStrategy = DropdownDrawingStrategy.JS,
  dropdownProps,
  dropdownListProps,
  maskOffset = {},
  isSimpleNavItem = false,
  onClick,
  ...rest
}: INavItemProps<LinkType>): JSX.Element {
  const maskRef = useRef();
  const rootClassName = classNames(
    "nav-item",
    {
      "nav-item--icon": type === "icon",
    },
    classes?.root,
  );

  const LinkComponent: FC<IIconLinkProps<LinkType> | ILinkProps<LinkType>> =
    iconData ? IconLink : Link;

  const animated = drawingStrategy === DropdownDrawingStrategy.JS;

  const RenderMask = useCallback(
    ({ opened, setOpened, buttonRef, pos }: IDropdownContext) => {
      if (!children) return null;
      const top = animated
        ? Number(pos?.top?.split("px")?.[0])
        : buttonRef?.current?.getBoundingClientRect()?.top;
      const content = (
        <DrawerMask
          data-testid="NavItemBtn__mask"
          ref={maskRef}
          classes={{ root: !animated ? "uds-appear-fade" : "" }}
          mods={["header"]}
          style={{
            top: (top || 0) + Number(maskOffset?.top || 0),
          }}
          onMouseEnter={() => setOpened(false)}
          onClick={() => setOpened(false)}
        />
      );
      return (
        <CSSTransition
          in={opened}
          classNames="uds-appear-fade"
          nodeRef={maskRef}
          timeout={300}
          mountOnEnter
          unmountOnExit
        >
          {content}
        </CSSTransition>
      );
    },
    [animated, children, maskOffset.top],
  );

  const IconComponent = useCallback(
    () => <MediaBuilder {...iconData} />,
    [iconData],
  );

  const simpleNavItem = (
    <li className={rootClassName}>
      <LinkComponent
        classes={{
          root: classNames("nav-link", { active }, classes?.link),
        }}
        onClick={onClick}
        data-testid="NavItemBtn"
        {...rest}
      />
    </li>
  );

  return isSimpleNavItem ? (
    simpleNavItem
  ) : (
    <li className={rootClassName}>
      <Dropdown
        dropdownButton={
          <DropdownContext.Consumer>
            {({ opened }) => (
              <LinkComponent
                classes={{
                  root: classNames(
                    "nav-link",
                    {
                      "nav-link--light": type === "light",
                      "nav-link--hover": opened,
                      "nav-link--active": active,
                      active,
                    },
                    classes?.link,
                  ),
                }}
                data-testid="NavItemBtn"
                linkParams={linkParams}
                {...(iconData ? { icon: IconComponent } : {})}
                {...rest}
              >
                {linkChildren}
              </LinkComponent>
            )}
          </DropdownContext.Consumer>
        }
        alignment={DropdownAlignment.STRETCH}
        toggleStrategy={DropdownToggleStrategy.HOVER}
        drawingStrategy={drawingStrategy}
        onClick={onClick}
        {...dropdownProps}
      >
        <DropdownList mask={RenderMask} {...dropdownListProps}>
          {() =>
            children && (
              <div
                className={classNames(
                  "header-main",
                  {
                    "container-fluid":
                      drawingStrategy === DropdownDrawingStrategy.JS,
                  },
                  classes?.wrapper,
                )}
              >
                <div
                  className={classNames(
                    "flyout-menu position-relative left-0 top-0 d-block",
                    classes?.menu,
                  )}
                >
                  {children}
                </div>
              </div>
            )
          }
        </DropdownList>
      </Dropdown>
    </li>
  );
}

export default NavItem;
